import { Link } from "react-router-dom";
import { ArrowRight, Flame, BookOpen, Users, CalendarDays, Lock } from "lucide-react";
import { useApi, fmtDateTime, fmtDate, imgSrc } from "../lib/api";
import { Spinner, ErrorBox, AccessBadge } from "../components/common";

const PERKS = [
  [Flame, "Kiln time & studio space", "Find shared kilns, studio benches and firing crews near you."],
  [Users, "Staff-reviewed introductions", "Our team connects you with galleries, studios and collectors — never automatically."],
  [BookOpen, "A working library", "Glaze recipes, firing schedules, contracts and market reports from practising makers."],
  [CalendarDays, "Events & studio circles", "Workshops, critiques, open studios and four topic circles with forums."],
];

function Hero() {
  return (
    <section className="kh-container grid items-center gap-10 pb-16 pt-10 lg:grid-cols-12 lg:pt-16">
      <div className="fade-up lg:col-span-6">
        <p className="eyebrow mb-4">A professional guild for ceramics</p>
        <h1 className="text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Where makers, studios and galleries <em className="text-celadon-dark">find each other</em></h1>
        <p className="mt-6 max-w-xl text-base text-[#4a4f59] md:text-lg">Kilnhouse Guild connects independent ceramic artists who need kiln time, studio space, commissions and representation with the studios, galleries and collectors who offer them.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/register" className="btn-ochre px-6 py-3 text-base" data-testid="hero-join-btn">Join the Guild <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          <Link to="/membership" className="btn-ghost px-6 py-3 text-base" data-testid="hero-membership-btn">Compare memberships</Link>
        </div>
      </div>
      <div className="relative lg:col-span-6">
        <img src="/img/p1.jpg" alt="Hand-thrown glazed stoneware vessels arranged on a studio shelf" className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-xl" />
        <div className="absolute -bottom-6 left-4 hidden rounded-2xl border border-line bg-white p-4 shadow-lg sm:block">
          <p className="font-serif text-2xl text-ink">Kiln time found</p>
          <p className="text-xs text-[#5a5f69]">Pacific Northwest · introduced by staff</p>
        </div>
      </div>
    </section>
  );
}

const SectionHead = ({ eyebrow, title, to, label }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
    <div><p className="eyebrow mb-2">{eyebrow}</p><h2 className="text-3xl sm:text-4xl">{title}</h2></div>
    {to && <Link to={to} className="text-sm font-semibold text-ink underline decoration-ochre underline-offset-4">{label}</Link>}
  </div>
);

export default function Home() {
  const { data, loading, error, reload } = useApi("/home");
  return (
    <div>
      <Hero />
      <section className="bg-white/70 py-16" aria-labelledby="perks-h">
        <div className="kh-container">
          <p className="eyebrow mb-2">What members get</p>
          <h2 id="perks-h" className="mb-10 max-w-2xl text-3xl sm:text-4xl">Everything a studio practice needs, in one guild.</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PERKS.map(([Icon, t, d], i) => (
              <div key={t} className="kh-card fade-up p-6" style={{ animationDelay: `${i * 80}ms` }}>
                <Icon className="mb-4 h-7 w-7 text-ochre-dark" aria-hidden="true" />
                <h3 className="text-xl">{t}</h3>
                <p className="mt-2 text-sm text-[#4a4f59]">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {loading ? <Spinner /> : error ? <div className="kh-container py-10"><ErrorBox message={error} onRetry={reload} /></div> : (
        <>
          <section className="kh-container py-16">
            <SectionHead eyebrow="Upcoming" title="Workshops & open studios" to="/events" label="Full calendar" />
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {data.events.map((e) => (
                <Link key={e.id} to={`/events/${e.slug}`} className="kh-card group overflow-hidden" data-testid={`home-event-${e.slug}`}>
                  <img src={imgSrc(e.cover)} alt="" className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                  <div className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-celadon-dark">{e.type}</p>
                    <h3 className="mt-1 text-lg leading-snug">{e.title}</h3>
                    <p className="mt-2 text-xs text-[#5a5f69]">{fmtDateTime(e.start)}</p>
                    {e.locked && <span className="chip-ochre mt-3"><Lock className="h-3 w-3" aria-hidden="true" />{e.access === "invite" ? "Invitation only" : "Members only"}</span>}
                  </div>
                </Link>
              ))}
            </div>
          </section>
          <section className="bg-ink py-16 text-paper">
            <div className="kh-container">
              <p className="eyebrow mb-2 text-celadon">From the library</p>
              <h2 className="mb-8 text-3xl text-paper sm:text-4xl">Featured resources</h2>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {data.library.map((i) => (
                  <Link key={i.id} to={`/library/${i.slug}`} className="rounded-2xl border border-white/10 bg-white/5 p-5 transition-colors hover:bg-white/10" data-testid={`home-library-${i.slug}`}>
                    <p className="text-xs uppercase tracking-wider text-celadon">{i.type}</p>
                    <h3 className="mt-2 text-lg leading-snug text-paper">{i.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm text-paper/75">{i.summary}</p>
                    <div className="mt-4"><AccessBadge level={i.access_level} locked={i.locked} /></div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
          <section className="kh-container py-16">
            <SectionHead eyebrow="News" title="Latest from the Guild" to="/news" label="All news" />
            <div className="grid gap-6 md:grid-cols-3">
              {data.news.map((n) => (
                <Link key={n.id} to={`/news/${n.slug}`} className="kh-card group overflow-hidden" data-testid={`home-news-${n.slug}`}>
                  <img src={imgSrc(n.cover)} alt="" className="h-44 w-full object-cover" />
                  <div className="p-5"><p className="text-xs text-[#5a5f69]">{fmtDate(n.date)}</p><h3 className="mt-1 text-xl">{n.title}</h3><p className="mt-2 text-sm text-[#4a4f59]">{n.excerpt}</p></div>
                </Link>
              ))}
            </div>
          </section>
          <section className="kh-container">
            <div className="rounded-[2rem] border border-line bg-sand/70 p-8 sm:p-12">
              <p className="eyebrow mb-3">Partner galleries</p>
              <ul className="grid gap-6 sm:grid-cols-3" data-testid="partner-galleries">
                {data.partners.map((p) => <li key={p.id}><p className="font-serif text-2xl text-ink">{p.name}</p><p className="text-sm text-[#5a5f69]">{p.city}</p></li>)}
              </ul>
              <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-8">
                <p className="font-serif text-2xl text-ink sm:text-3xl">Ready to find your people in clay?</p>
                <Link to="/register" className="btn-primary px-6 py-3" data-testid="bottom-join-btn">Join the Guild</Link>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
